import { useState, useMemo } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  BookOpen,
  CheckCircle2,
  XCircle,
  Clock,
  Trash2,
  Eye,
  FileText,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/billingUtils";

interface JournalEntry {
  id: string;
  entry_number: number;
  entry_date: string;
  description: string;
  reference?: string;
  reference_type?: string;
  total_debit: number;
  total_credit: number;
  status: 'draft' | 'posted' | 'reversed';
  created_at: string;
  posted_at?: string;
}

interface JournalEntryLine {
  id?: string;
  entry_id?: string;
  line_number: number;
  account_id: string;
  account_code: string;
  account_name?: string;
  debit: number;
  credit: number;
  description?: string;
}

interface AccountingAccount {
  id: string;
  code: string;
  name: string;
  account_type: string;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  posted: 'Contabilizado',
  reversed: 'Anulado',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  posted: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  reversed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const emptyLine: JournalEntryLine = {
  line_number: 1,
  account_id: '',
  account_code: '',
  debit: 0,
  credit: 0,
  description: '',
};

export default function JournalEntries() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 7));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [selectedLines, setSelectedLines] = useState<JournalEntryLine[]>([]);

  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [entryDescription, setEntryDescription] = useState('');
  const [entryReference, setEntryReference] = useState('');
  const [lines, setLines] = useState<JournalEntryLine[]>([{ ...emptyLine }]);

  // Fetch journal entries
  const { data: entries = [], isLoading } = useQuery<JournalEntry[]>({
    queryKey: ["journal-entries", statusFilter, dateFilter],
    queryFn: async () => {
      let query = api
        .from("journal_entries")
        .select("*")
        .order("entry_date", { ascending: false })
        .order("entry_number", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      if (dateFilter) {
        const [year, month] = dateFilter.split('-');
        const startDate = `${year}-${month}-01`;
        const endDate = new Date(parseInt(year), parseInt(month), 0).toISOString().split('T')[0];
        query = query.gte("entry_date", startDate).lte("entry_date", endDate);
      }

      const { data, error } = await query.limit(200);
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch accounts for selector
  const { data: accounts = [] } = useQuery<AccountingAccount[]>({
    queryKey: ["accounting-accounts"],
    queryFn: async () => {
      const { data, error } = await api
        .from("accounting_accounts")
        .select("id, code, name, account_type")
        .order("code");
      if (error) throw error;
      return data || [];
    },
  });

  // Filter by search
  const filteredEntries = useMemo(() => {
    if (!searchTerm) return entries;
    const term = searchTerm.toLowerCase();
    return entries.filter(e =>
      e.description.toLowerCase().includes(term) ||
      e.reference?.toLowerCase().includes(term) ||
      e.entry_number?.toString().includes(term)
    );
  }, [entries, searchTerm]);

  // Stats
  const stats = useMemo(() => {
    const total = entries.length;
    const draft = entries.filter(e => e.status === 'draft').length;
    const posted = entries.filter(e => e.status === 'posted').length;
    const totalDebit = entries.reduce((sum, e) => sum + (e.total_debit || 0), 0);
    return { total, draft, posted, totalDebit };
  }, [entries]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!entryDescription) {
        throw new Error("La descripcion es obligatoria");
      }

      const validLines = lines.filter(l => l.account_id && (l.debit > 0 || l.credit > 0));
      if (validLines.length < 2) {
        throw new Error("El asiento debe tener al menos 2 lineas");
      }

      const totalDebit = validLines.reduce((sum, l) => sum + l.debit, 0);
      const totalCredit = validLines.reduce((sum, l) => sum + l.credit, 0);

      if (Math.abs(totalDebit - totalCredit) > 0.01) {
        throw new Error(`El asiento no cuadra. Debe: ${formatCurrency(totalDebit)}, Haber: ${formatCurrency(totalCredit)}`);
      }

      // Get next entry number
      const { data: maxNum } = await api
        .from("journal_entries")
        .select("entry_number")
        .order("entry_number", { ascending: false })
        .limit(1)
        .single();

      const nextNumber = (maxNum?.entry_number || 0) + 1;

      // Create entry
      const { data: entry, error: entryError } = await api
        .from("journal_entries")
        .insert({
          entry_number: nextNumber,
          entry_date: entryDate,
          description: entryDescription,
          reference: entryReference || null,
          total_debit: totalDebit,
          total_credit: totalCredit,
          status: 'draft',
          created_by: user?.id,
        })
        .select()
        .single();

      if (entryError) throw entryError;

      // Create lines
      const lineData = validLines.map((l, idx) => ({
        entry_id: entry.id,
        line_number: idx + 1,
        account_id: l.account_id,
        account_code: l.account_code,
        debit: l.debit,
        credit: l.credit,
        description: l.description || null,
      }));

      const { error: linesError } = await api
        .from("journal_entry_lines")
        .insert(lineData);

      if (linesError) throw linesError;

      return entry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
      resetForm();
      setDialogOpen(false);
      toast.success("Asiento creado correctamente");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  // Post mutation
  const postMutation = useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await api
        .from("journal_entries")
        .update({
          status: 'posted',
          posted_at: new Date().toISOString(),
          posted_by: user?.id,
        })
        .eq("id", entryId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
      toast.success("Asiento contabilizado");
    },
    onError: () => {
      toast.error("Error al contabilizar");
    },
  });

  // View entry lines
  const viewEntry = async (entry: JournalEntry) => {
    setSelectedEntry(entry);

    const { data } = await api
      .from("journal_entry_lines")
      .select("*, accounting_accounts(name)")
      .eq("entry_id", entry.id)
      .order("line_number");

    const linesWithNames = (data || []).map((l: any) => ({
      ...l,
      account_name: l.accounting_accounts?.name || '',
    }));

    setSelectedLines(linesWithNames);
    setViewDialogOpen(true);
  };

  const resetForm = () => {
    setEntryDate(new Date().toISOString().split('T')[0]);
    setEntryDescription('');
    setEntryReference('');
    setLines([{ ...emptyLine }]);
  };

  const addLine = () => {
    setLines([...lines, { ...emptyLine, line_number: lines.length + 1 }]);
  };

  const removeLine = (idx: number) => {
    if (lines.length > 1) {
      setLines(lines.filter((_, i) => i !== idx));
    }
  };

  const updateLine = (idx: number, field: keyof JournalEntryLine, value: any) => {
    const newLines = [...lines];
    newLines[idx] = { ...newLines[idx], [field]: value };

    // If account selected, update code
    if (field === 'account_id') {
      const account = accounts.find(a => a.id === value);
      if (account) {
        newLines[idx].account_code = account.code;
      }
    }

    setLines(newLines);
  };

  const totals = useMemo(() => {
    const debit = lines.reduce((sum, l) => sum + (l.debit || 0), 0);
    const credit = lines.reduce((sum, l) => sum + (l.credit || 0), 0);
    return { debit, credit, balanced: Math.abs(debit - credit) < 0.01 };
  }, [lines]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Libro Diario</h1>
            <p className="text-muted-foreground">
              Asientos contables y movimientos
            </p>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Asiento
          </Button>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Asientos</CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Borradores</CardTitle>
              <Clock className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{stats.draft}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Contabilizados</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.posted}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Debe</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(stats.totalDebit)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por descripcion, referencia..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="posted">Contabilizado</SelectItem>
                  <SelectItem value="reversed">Anulado</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="month"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-[180px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Num</TableHead>
                  <TableHead className="w-[100px]">Fecha</TableHead>
                  <TableHead>Descripcion</TableHead>
                  <TableHead className="w-[120px]">Referencia</TableHead>
                  <TableHead className="w-[120px] text-right">Debe</TableHead>
                  <TableHead className="w-[120px] text-right">Haber</TableHead>
                  <TableHead className="w-[100px]">Estado</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      Cargando...
                    </TableCell>
                  </TableRow>
                ) : filteredEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No hay asientos en este periodo
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-mono">{entry.entry_number}</TableCell>
                      <TableCell>{format(new Date(entry.entry_date), 'dd/MM/yyyy')}</TableCell>
                      <TableCell className="max-w-[300px] truncate">{entry.description}</TableCell>
                      <TableCell className="text-muted-foreground">{entry.reference || '-'}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(entry.total_debit)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(entry.total_credit)}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[entry.status]}>
                          {STATUS_LABELS[entry.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewEntry(entry)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {entry.status === 'draft' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => postMutation.mutate(entry.id)}
                            >
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* New Entry Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nuevo Asiento Contable</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Fecha</Label>
                  <Input
                    type="date"
                    value={entryDate}
                    onChange={(e) => setEntryDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Descripcion *</Label>
                  <Input
                    value={entryDescription}
                    onChange={(e) => setEntryDescription(e.target.value)}
                    placeholder="Descripcion del asiento"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Referencia</Label>
                <Input
                  value={entryReference}
                  onChange={(e) => setEntryReference(e.target.value)}
                  placeholder="Factura, albaran, etc."
                />
              </div>

              {/* Lines */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Lineas del asiento</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addLine}>
                    <Plus className="w-4 h-4 mr-1" /> Agregar linea
                  </Button>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[300px]">Cuenta</TableHead>
                      <TableHead className="w-[200px]">Concepto</TableHead>
                      <TableHead className="w-[120px]">Debe</TableHead>
                      <TableHead className="w-[120px]">Haber</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Select
                            value={line.account_id}
                            onValueChange={(val) => updateLine(idx, 'account_id', val)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar cuenta..." />
                            </SelectTrigger>
                            <SelectContent>
                              {accounts.map((acc) => (
                                <SelectItem key={acc.id} value={acc.id}>
                                  {acc.code} - {acc.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={line.description || ''}
                            onChange={(e) => updateLine(idx, 'description', e.target.value)}
                            placeholder="Concepto"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.debit || ''}
                            onChange={(e) => updateLine(idx, 'debit', parseFloat(e.target.value) || 0)}
                            className="text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.credit || ''}
                            onChange={(e) => updateLine(idx, 'credit', parseFloat(e.target.value) || 0)}
                            className="text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLine(idx)}
                            disabled={lines.length === 1}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals row */}
                    <TableRow className="font-bold bg-muted/50">
                      <TableCell colSpan={2} className="text-right">
                        TOTALES
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(totals.debit)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(totals.credit)}
                      </TableCell>
                      <TableCell>
                        {totals.balanced ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {!totals.balanced && (
                  <p className="text-sm text-red-600">
                    Diferencia: {formatCurrency(Math.abs(totals.debit - totals.credit))}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !totals.balanced}
              >
                {saveMutation.isPending ? "Guardando..." : "Guardar Asiento"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Entry Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                Asiento #{selectedEntry?.entry_number}
              </DialogTitle>
            </DialogHeader>

            {selectedEntry && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Fecha:</span>{' '}
                    {format(new Date(selectedEntry.entry_date), 'dd/MM/yyyy')}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Referencia:</span>{' '}
                    {selectedEntry.reference || '-'}
                  </div>
                  <div>
                    <Badge className={STATUS_COLORS[selectedEntry.status]}>
                      {STATUS_LABELS[selectedEntry.status]}
                    </Badge>
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground">Descripcion:</span>{' '}
                  {selectedEntry.description}
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cuenta</TableHead>
                      <TableHead>Concepto</TableHead>
                      <TableHead className="text-right">Debe</TableHead>
                      <TableHead className="text-right">Haber</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedLines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>
                          <span className="font-mono">{line.account_code}</span>{' '}
                          {line.account_name}
                        </TableCell>
                        <TableCell>{line.description || '-'}</TableCell>
                        <TableCell className="text-right font-mono">
                          {line.debit > 0 ? formatCurrency(line.debit) : ''}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {line.credit > 0 ? formatCurrency(line.credit) : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-bold bg-muted/50">
                      <TableCell colSpan={2} className="text-right">TOTALES</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(selectedEntry.total_debit)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(selectedEntry.total_credit)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
